// Lifetime career statistics (design doc 16.76) -- what the Captain's
// Lounge reports back to the captain about their own record.
//
// Deliberately raw counters, incremented at the exact points the real
// events already resolve (a mission actually completing, a hostile
// actually being destroyed), never recomputed or estimated afterwards --
// a career stat that disagrees with what the captain remembers doing is
// worse than no stat at all.
//
// Anything genuinely derivable from existing state is NOT stored here:
// systems visited comes from `visitedSystemIds`, fleet size from `fleet`,
// skill levels from `captainSkills`. Only things with no other record
// earn a counter.

export interface CaptainStats {
  missionsCompleted: number;
  missionsAbandoned: number;
  hostilesDestroyed: number;
  dronesLost: number;
  oreMinedM3: number;
}

export function createCaptainStats(): CaptainStats {
  return {
    missionsCompleted: 0,
    missionsAbandoned: 0,
    hostilesDestroyed: 0,
    dronesLost: 0,
    oreMinedM3: 0
  };
}

// Spread over a fresh set rather than trusting a loaded blob wholesale:
// a pre-16.76 save has no stats block at all, and a save written by a
// later build that adds a counter would otherwise load it as undefined
// and turn every subsequent increment into NaN.
export function normalizeCaptainStats(raw: Partial<CaptainStats> | undefined): CaptainStats {
  const base = createCaptainStats();
  if (!raw) return base;
  const merged = { ...base, ...raw };
  (Object.keys(base) as (keyof CaptainStats)[]).forEach((key) => {
    const value = merged[key];
    merged[key] = typeof value === "number" && Number.isFinite(value) ? value : 0;
  });
  return merged;
}

// Total missions actually resolved one way or the other -- used for the
// success-rate readout. Missions still in progress deliberately don't
// count toward either side of it.
export function missionsResolved(stats: CaptainStats): number {
  return stats.missionsCompleted + stats.missionsAbandoned;
}

export function missionSuccessRate(stats: CaptainStats): number | null {
  const resolved = missionsResolved(stats);
  return resolved === 0 ? null : stats.missionsCompleted / resolved;
}
